/**
 * Main App Component
 * 
 * Configures React Router with all application routes
 * - Public routes (landing page)
 * - Protected routes (dashboard, events, etc.)
 * - Coach routes (check-in, riders)
 * - Admin routes (users, configuration, season)
 * 
 * Requirements: 1.3, 1.12
 */

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ProtectedRoute, LoginPage } from './auth/ProtectedRoute';
import PhoneNumberPromptModal from './components/auth/PhoneNumberPromptModal';
import { needsPhonePrompt } from './utils/phone';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastContainer } from './components/common/Toast';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from './components/common/KeyboardShortcutsHelp';
import { ScrollToTop } from './components/common/ScrollToTop';
import { useGroupLeaderStore } from './store/groupLeaderStore';

// Layout Components
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { PWAPrompt } from './components/common/PWAPrompt';
import { OfflineIndicator } from './components/common/OfflineIndicator';

// Public Pages
import LandingPage from './pages/LandingPage';
import RecoverAccess from './pages/RecoverAccess';

// Authenticated Pages
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import EventCreate from './pages/EventCreate';
import EventEdit from './pages/EventEdit';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import RSVP from './pages/RSVP';
import EventIncidents from './pages/EventIncidents';

// Coach Pages
import Checkin from './pages/Checkin';
import Riders from './pages/Riders';
import RiderDetail from './pages/RiderDetail';
import RiderCreate from './pages/RiderCreate';
import RiderEdit from './pages/RiderEdit';
import Results from './pages/Results';
import SkillsReference from './pages/SkillsReference';
import SkillsImport from './pages/SkillsImport';

// Admin Pages
import Users from './pages/admin/Users';
import Configuration from './pages/admin/Configuration';
import SeasonManagement from './pages/admin/SeasonManagement';
import Reports from './pages/admin/Reports';
import ActiveRidersReport from './pages/admin/ActiveRidersReport';
import CoachesReport from './pages/admin/CoachesReport';
import RidersReport from './pages/admin/RidersReport';
import SkillsReport from './pages/admin/SkillsReport';
import GroupMembershipReport from './pages/admin/GroupMembershipReport';
import RaceRegistrationReport from './pages/admin/RaceRegistrationReport';
import IncidentsReport from './pages/admin/IncidentsReport';
import RsvpReminderTest from './pages/admin/RsvpReminderTest';
import MotdAdmin from './pages/admin/MotdAdmin';
import MotdBanner from './components/motd/MotdBanner';

/**
 * Layout wrapper for authenticated pages
 * Includes header and navigation
 */
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const fetchGroupLeaders = useGroupLeaderStore(s => s.fetchGroupLeaders);

  // Load group leader data on mount
  useEffect(() => {
    fetchGroupLeaders();
  }, []);

  // Reset dismissed state on route change so the modal reappears
  useEffect(() => {
    setDismissed(false);
  }, [location]);

  const showPrompt = needsPhonePrompt(user) && !dismissed;

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Sticky header and navigation container */}
      <div className="sticky top-0 z-50 bg-white shadow">
        <Header />
        <Navigation />
      </div>
      <main id="main-content" className="container mx-auto px-4 pt-3 pb-8" tabIndex={-1}>
        {children}
      </main>
      <MotdBanner />
      <PhoneNumberPromptModal
        isOpen={showPrompt}
        onClose={() => setDismissed(true)}
        onSaved={() => refreshUser()}
      />
    </div>
  );
}

/**
 * App Content Component
 * Includes keyboard shortcuts hook
 */
function AppContent() {
  // Enable keyboard shortcuts
  useKeyboardShortcuts();

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Toast Notifications */}
      <ToastContainer />
      
      {/* Offline Indicator */}
      <OfflineIndicator />
      
      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />
      
      <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/recover" element={<RecoverAccess />} />

          {/* Protected Routes - Authenticated Users */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Dashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Calendar />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Events />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:id"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <EventDetail />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:id/edit"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <EventEdit />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Leaderboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Profile />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rsvp"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <RSVP />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Coach Routes - Requires coach or admin role */}
          <Route
            path="/checkin"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <Checkin />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkin/:eventId"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <Checkin />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/time-trials"
            element={<Navigate to="/results?tab=time-trials" replace />}
          />
          <Route
            path="/results"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <Results />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skills"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <SkillsReference />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skills/import"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <SkillsImport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/riders"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Riders />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/riders/:id"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <RiderDetail />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/riders/create"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <RiderCreate />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/riders/:id/edit"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <RiderEdit />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - Requires admin role */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Users />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/events"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Events />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/events/create"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <EventCreate />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/events/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <EventEdit />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/riders"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Riders />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/configuration"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Configuration />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/season"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <SeasonManagement />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <Reports />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/active-riders"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <ActiveRidersReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/coaches"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <CoachesReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/riders"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <RidersReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/skills"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <SkillsReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/group-membership"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <GroupMembershipReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/race-registration"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <RaceRegistrationReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/incidents"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <IncidentsReport />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/incidents"
            element={
              <ProtectedRoute requireCoach>
                <AuthenticatedLayout>
                  <EventIncidents />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/rsvp-reminder-test"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <RsvpReminderTest />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/motd"
            element={
              <ProtectedRoute requireAdmin>
                <AuthenticatedLayout>
                  <MotdAdmin />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect to dashboard for authenticated users, landing page for others */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* PWA Update Prompt */}
        <PWAPrompt />
      </>
    );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
